import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  attachProofRecommendation,
  createClient,
  createNode,
  createPartner,
  confirmClientReportSent,
  generateProofPacket,
  generatePulsePacket,
  getClientPerformance,
  getAwaitingClientReportDeliveries,
  provisionClientGa4,
  getProofPacketDetail,
  getCallerSheetSources,
  getGoogleSheetsConfig,
  getProofPackets,
  getPulsePackets,
  importCsv,
  processReports,
  processTelegramOutbox,
  registerCallerSheet,
  recordAutomationMetrics,
  saveClientConnections,
  syncClientPerformance,
  syncGoogleSheets,
  updateProofPacketStatus,
  updatePulsePacketStatus,
  updateReportSchedule
} from "../lib/api";
import type { AwaitingClientReportDelivery } from "../lib/api";
import type {
  AutomationMetricSnapshot,
  CallerSheetSource,
  ClientPerformanceData,
  ClientRecord,
  CrmEvent,
  CurrentUser,
  GoogleSheetsConfig,
  NodeRecord,
  PartnerRecord,
  Permissions,
  ProofPacket,
  ProofPacketDetail,
  ProofRecommendation,
  PulsePacket,
  TrackingOwnership,
  WarmEntity,
  WarmEntityType
} from "../lib/types";
import { Icon } from "./Icon";

export function PageHeader({ title, description, action }: { title: string; description: string; action?: React.ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action ? <div className="page-action">{action}</div> : null}
    </header>
  );
}

export function OverviewPage({
  clients,
  partners,
  nodes,
  events,
  onSelect
}: {
  clients: ClientRecord[];
  partners: PartnerRecord[];
  nodes: NodeRecord[];
  events: CrmEvent[];
  onSelect: (type: WarmEntityType, entity: WarmEntity) => void;
}) {
  const paidClients = clients.filter((client) => ["paid", "subscribed", "website_created", "active"].includes(client.stage));
  const dueFollowUps = [...clients, ...partners, ...nodes].filter((item) => item.next_follow_up_at);

  return (
    <div className="page-stack">
      <PageHeader title="Warm Engine" description="Track clients, referral partners, and community nodes without exposing owner-only records to user accounts." />
      <section className="kpi-strip warm-kpis">
        <Kpi label="Clients" value={clients.length} hint={`${paidClients.length} paying or active`} />
        <Kpi label="Partners" value={partners.length} hint={`${partners.filter((partner) => partner.relationship_stage === "active").length} active`} />
        <Kpi label="Nodes" value={nodes.length} hint={`${nodes.filter((node) => node.relationship_stage === "active").length} active`} />
        <Kpi label="Follow-ups" value={dueFollowUps.length} hint="dated next actions" />
      </section>
      <div className="dashboard-grid warm-grid">
        <section className="panel board-panel">
          <div className="panel-header">
            <h2>Client Stage Board</h2>
          </div>
          <StageBoard clients={clients} onSelect={(client) => onSelect("client", client)} />
        </section>
        <aside className="today-rail">
          <section className="panel">
            <div className="panel-header">
              <h2>Next Follow-ups</h2>
            </div>
            <div className="mini-list">
              {dueFollowUps.length === 0 ? <EmptyInline text="No dated follow-ups yet." /> : null}
              {dueFollowUps.slice(0, 6).map((item) => (
                <button className="mini-item row-button" key={`${"display_name" in item ? "client" : "node_type" in item ? "node" : "partner"}-${item.id}`} onClick={() => {
                  const type: WarmEntityType = "display_name" in item ? "client" : "node_type" in item ? "node" : "partner";
                  onSelect(type, item);
                }}>
                  <Icon name="clock" />
                  <div>
                    <strong>{entityName(item)}</strong>
                    <span>{item.follow_up_notes || item.candidate_justification || "Review next action"}</span>
                  </div>
                  <time>{shortDate(item.next_follow_up_at)}</time>
                </button>
              ))}
            </div>
          </section>
          <section className="panel">
            <div className="panel-header">
              <h2>Recent Events</h2>
            </div>
            <EventList events={events.slice(0, 5)} compact />
          </section>
        </aside>
      </div>
    </div>
  );
}

export function ClientsPage({
  clients,
  partners,
  nodes,
  query,
  permissions,
  selectedId,
  onSelect,
  onCreated
}: {
  clients: ClientRecord[];
  partners: PartnerRecord[];
  nodes: NodeRecord[];
  query: string;
  permissions: Permissions | null;
  selectedId?: string;
  onSelect: (client: ClientRecord) => void;
  onCreated: (client: ClientRecord) => void;
}) {
  const rows = filterRows(clients, query);
  return (
    <div className="page-stack">
      <PageHeader title="Clients" description="Warm prospects, paid clients, subscriptions, website builds, and follow-up state." />
      <EntityTable
        type="client"
        rows={rows}
        selectedId={selectedId}
        onSelect={(entity) => onSelect(entity as ClientRecord)}
        columns={["Stage", "Service", "Contact", "Next follow-up", "Source"]}
      />
      <ClientCreateForm clients={clients} partners={partners} nodes={nodes} permissions={permissions} onCreated={onCreated} />
    </div>
  );
}

export function FollowUpsPage({
  clients,
  selectedId,
  onSelect
}: {
  clients: ClientRecord[];
  selectedId?: string;
  onSelect: (client: ClientRecord) => void;
}) {
  const { newLeads, dueLeads } = useMemo(() => splitFollowUps(clients), [clients]);
  return (
    <div className="page-stack">
      <PageHeader
        title="Follow-ups"
        description="Your daily list. New leads waiting on a first message, and anyone whose follow-up date has arrived."
      />

      <section className="panel">
        <div className="panel-head">
          <h2>New leads to contact ({newLeads.length})</h2>
          <p className="muted">Warm prospects nobody has messaged yet. Aim to reach these within a day.</p>
        </div>
        <EntityTable
          type="client"
          rows={newLeads}
          selectedId={selectedId}
          onSelect={(entity) => onSelect(entity as ClientRecord)}
          columns={["Stage", "Service", "Contact", "Next follow-up", "Source"]}
        />
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>Follow-ups due now ({dueLeads.length})</h2>
          <p className="muted">Their follow-up date has arrived or passed. Message them, then set the next date.</p>
        </div>
        <EntityTable
          type="client"
          rows={dueLeads}
          selectedId={selectedId}
          onSelect={(entity) => onSelect(entity as ClientRecord)}
          columns={["Stage", "Service", "Contact", "Next follow-up", "Source"]}
        />
      </section>
    </div>
  );
}

export function PartnersPage({
  partners,
  query,
  selectedId,
  onSelect,
  onCreated
}: {
  partners: PartnerRecord[];
  query: string;
  selectedId?: string;
  onSelect: (partner: PartnerRecord) => void;
  onCreated: (partner: PartnerRecord) => void;
}) {
  const rows = filterRows(partners, query);
  return (
    <div className="page-stack">
      <PageHeader title="Partners" description="Businesses and organizations that can refer clients or open channels." />
      <EntityTable type="partner" rows={rows} selectedId={selectedId} onSelect={(entity) => onSelect(entity as PartnerRecord)} columns={["Stage", "Type", "Contact", "Next follow-up", "Fit"]} />
      <RelationshipCreateForm type="partner" onCreated={(entity) => onCreated(entity as PartnerRecord)} />
    </div>
  );
}

export function NodesPage({
  nodes,
  query,
  selectedId,
  onSelect,
  onCreated
}: {
  nodes: NodeRecord[];
  query: string;
  selectedId?: string;
  onSelect: (node: NodeRecord) => void;
  onCreated: (node: NodeRecord) => void;
}) {
  const rows = filterRows(nodes, query);
  return (
    <div className="page-stack">
      <PageHeader title="Nodes" description="Individual gatekeepers, community connectors, hosts, and local trust builders." />
      <EntityTable type="node" rows={rows} selectedId={selectedId} onSelect={(entity) => onSelect(entity as NodeRecord)} columns={["Stage", "Type", "Contact", "Next follow-up", "Context"]} />
      <RelationshipCreateForm type="node" onCreated={(entity) => onCreated(entity as NodeRecord)} />
    </div>
  );
}

export function AssignmentsPage({
  clients,
  partners,
  nodes
}: {
  clients: ClientRecord[];
  partners: PartnerRecord[];
  nodes: NodeRecord[];
}) {
  const rows = [
    ...clients.map((entity) => ({ type: "client", name: entity.display_name, owner: entity.created_by_user_id, next: entity.next_follow_up_at })),
    ...partners.map((entity) => ({ type: "partner", name: entity.name, owner: entity.created_by_user_id, next: entity.next_follow_up_at })),
    ...nodes.map((entity) => ({ type: "node", name: entity.name, owner: entity.created_by_user_id, next: entity.next_follow_up_at }))
  ];
  return (
    <div className="page-stack">
      <PageHeader title="Team workspace" description="All shared client, partner, and node records. Follow-up work can be picked up by any user." />
      <section className="panel table-panel entity-table-panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Record</th>
                <th>Type</th>
                <th>Added by</th>
                <th>Next follow-up</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.type}-${row.name}`}>
                  <td data-label="Record">{row.name}</td>
                  <td data-label="Type"><span className="status-pill neutral">{row.type}</span></td>
                  <td data-label="Added by">{row.owner || "Built Locally"}</td>
                  <td data-label="Next follow-up">{shortDate(row.next)}</td>
                </tr>
              ))}
              {rows.length === 0 ? <tr><td className="empty-table" colSpan={4}>No assigned records are visible.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export function EventsPage({ events }: { events: CrmEvent[] }) {
  return (
    <div className="page-stack">
      <PageHeader title="Events" description="Stage changes, source operations, and suppressed notification decisions." />
      <section className="panel">
        <EventList events={events} />
      </section>
    </div>
  );
}

export function ReportsPage({
  clients,
  permissions,
  onNotice
}: {
  clients: ClientRecord[];
  permissions: Permissions | null;
  onNotice: (notice: string) => void;
}) {
  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [recipientEmail, setRecipientEmail] = useState(clients[0]?.email || "");
  const [nextDueAt, setNextDueAt] = useState("");
  const [awaitingDeliveries, setAwaitingDeliveries] = useState<AwaitingClientReportDelivery[]>([]);
  const canManage = permissions?.canChangeClientStage;

  async function loadAwaitingDeliveries() {
    if (!canManage) return;
    const result = await getAwaitingClientReportDeliveries();
    setAwaitingDeliveries(result.deliveries);
  }

  useEffect(() => {
    void loadAwaitingDeliveries();
  }, [canManage]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!clientId || !canManage) return;
    await updateReportSchedule(clientId, { enabled: true, cadence: "biweekly", recipientEmail, nextDueAt });
    onNotice("Report schedule saved.");
  }

  async function runReports() {
    await processReports();
    await loadAwaitingDeliveries();
    onNotice("Report packet queued. Confirm once the client email has been sent.");
  }

  async function confirmDelivery(id: string, clientName: string) {
    await confirmClientReportSent(id);
    await loadAwaitingDeliveries();
    onNotice(`Client report marked sent: ${clientName}.`);
  }

  return (
    <div className="page-stack">
      <PageHeader title="Reports" description="Biweekly report-due schedules that queue Telegram packets for owner review before any client email is written." />
      <section className="split-panels">
        <form className="panel form-panel" onSubmit={submit}>
          <h2>Schedule Report Packet</h2>
          <label>
            Client
            <select value={clientId} onChange={(event) => {
              const selected = clients.find((client) => client.id === event.target.value);
              setClientId(event.target.value);
              setRecipientEmail(selected?.email || "");
            }}>
              {clients.map((client) => <option value={client.id} key={client.id}>{client.display_name}</option>)}
            </select>
          </label>
          <label>
            Recipient email later
            <input value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)} placeholder="client@example.com" disabled={!canManage} />
          </label>
          <label>
            Next due date
            <input type="datetime-local" value={nextDueAt} onChange={(event) => setNextDueAt(event.target.value)} disabled={!canManage} />
          </label>
          <button type="submit" disabled={!canManage}>Save schedule</button>
        </form>
        <section className="panel report-list">
          <div className="panel-header">
            <h2>Report Candidates</h2>
            {canManage ? <button onClick={runReports}>Process due</button> : null}
          </div>
          {clients.map((client) => (
            <div className="report-row" key={client.id}>
              <span>{client.display_name}</span>
              <strong>{client.email || "No email"}</strong>
            </div>
          ))}
        </section>
      </section>
      <section className="panel report-list">
        <div className="panel-header">
          <div>
            <h2>Awaiting Client Email</h2>
            <p>After you send the drafted email, confirm it here to record the delivery and set the next due date.</p>
          </div>
        </div>
        {awaitingDeliveries.length ? awaitingDeliveries.map((delivery) => (
          <div className="report-row" key={delivery.id}>
            <span>{delivery.client_name}</span>
            <strong>{delivery.recipient_email || "No email"}</strong>
            {canManage ? <button onClick={() => void confirmDelivery(delivery.id, delivery.client_name)}>Confirm email sent</button> : null}
          </div>
        )) : <p className="muted">No client report emails are awaiting confirmation.</p>}
      </section>
    </div>
  );
}

export function PerformancePage({
  clients,
  permissions,
  onNotice
}: {
  clients: ClientRecord[];
  permissions: Permissions | null;
  onNotice: (notice: string) => void;
}) {
  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [data, setData] = useState<ClientPerformanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingConnection, setSavingConnection] = useState(false);
  const [provisioningGa4, setProvisioningGa4] = useState(false);
  const [savingAutomation, setSavingAutomation] = useState(false);
  const [ownershipModel, setOwnershipModel] = useState<TrackingOwnership>("builtlocally_managed");
  const [ga4PropertyId, setGa4PropertyId] = useState("");
  const [ga4MeasurementId, setGa4MeasurementId] = useState("");
  const [ga4PropertyUrl, setGa4PropertyUrl] = useState("");
  const [searchConsolePropertyUrl, setSearchConsolePropertyUrl] = useState("");
  const [automationName, setAutomationName] = useState("Telegram booking notifications");
  const [automationType, setAutomationType] = useState("booking");
  const [periodStart, setPeriodStart] = useState(weekInput(-9));
  const [periodEnd, setPeriodEnd] = useState(weekInput(-3));
  const [enquiriesCount, setEnquiriesCount] = useState("0");
  const [bookingsCount, setBookingsCount] = useState("0");
  const [callsCount, setCallsCount] = useState("0");
  const [messagesCount, setMessagesCount] = useState("0");
  const [actionsCompleted, setActionsCompleted] = useState("0");
  const [errorCount, setErrorCount] = useState("0");
  const [automationNotes, setAutomationNotes] = useState("");
  const canManage = permissions?.canChangeClientStage;

  async function load() {
    if (!clientId || !canManage) return;
    setLoading(true);
    try {
      const next = await getClientPerformance(clientId);
      setData(next);
      const ga4 = next.sources.find((source) => source.source_kind === "ga4");
      const search = next.sources.find((source) => source.source_kind === "search_console");
      setOwnershipModel(ga4?.ownership_model || search?.ownership_model || "builtlocally_managed");
      setGa4PropertyId(ga4?.external_id || next.client.ga4_property_id || "");
      setGa4MeasurementId(next.client.ga4_measurement_id || "");
      setGa4PropertyUrl(ga4?.display_url || next.client.ga4_property_url || "");
      setSearchConsolePropertyUrl(search?.external_id || "");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load().catch((error) => onNotice(error instanceof Error ? error.message : "Could not load performance tracking."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, canManage]);

  async function saveConnections(event: FormEvent) {
    event.preventDefault();
    if (!clientId || !canManage) return;
    setSavingConnection(true);
    try {
      await saveClientConnections(clientId, { ownershipModel, ga4PropertyId, ga4MeasurementId, ga4PropertyUrl, searchConsolePropertyUrl });
      onNotice("Tracking connections saved. Use Sync website data to test access.");
      await load();
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Could not save tracking connections.");
    } finally {
      setSavingConnection(false);
    }
  }

  async function sync() {
    if (!clientId || !canManage) return;
    try {
      const result = await syncClientPerformance(clientId);
      const failed = Object.values(result.statuses).filter((value) => value !== "connected");
      onNotice(failed.length ? `Website data sync completed with ${failed.length} connection issue(s).` : `Website data synced for ${result.period.start} to ${result.period.end}.`);
      await load();
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Could not sync website data.");
    }
  }

  async function provisionGa4() {
    if (!clientId || !canManage) return;
    setProvisioningGa4(true);
    try {
      const result = await provisionClientGa4(clientId);
      onNotice(`GA4 created. Measurement ID ${result.measurementId} is saved. Install the tag on the live website, then use Sync website data.`);
      await load();
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Could not create GA4 tracking.");
    } finally {
      setProvisioningGa4(false);
    }
  }

  async function saveAutomation(event: FormEvent) {
    event.preventDefault();
    if (!clientId || !canManage) return;
    setSavingAutomation(true);
    try {
      await recordAutomationMetrics(clientId, {
        automationName,
        automationType,
        periodStart,
        periodEnd,
        enquiriesCount,
        bookingsCount,
        callsCount,
        messagesCount,
        actionsCompleted,
        errorCount,
        notes: automationNotes
      });
      onNotice("Automation results saved for this reporting period.");
      await load();
    } catch (error) {
      onNotice(error instanceof Error ? error.message : "Could not save automation results.");
    } finally {
      setSavingAutomation(false);
    }
  }

  const latest = data?.snapshots[0];
  const ga4Source = data?.sources.find((source) => source.source_kind === "ga4");
  const searchSource = data?.sources.find((source) => source.source_kind === "search_console");

  return (
    <div className="page-stack">
      <PageHeader title="Performance" description="Cloudflare Web Analytics is matched to each saved website domain, then weekly evidence powers Proof and Pulse." />
      <section className="panel performance-client-picker">
        <label>
          Client
          <select value={clientId} onChange={(event) => setClientId(event.target.value)} disabled={!canManage}>
            {clients.map((client) => <option value={client.id} key={client.id}>{client.display_name}</option>)}
          </select>
        </label>
        <div className="performance-picker-summary">
          <strong>{data?.client.website_url || "No website saved"}</strong>
          <span>{loading ? "Loading tracking" : latest ? `Latest website data: ${latest.period_start} to ${latest.period_end}` : "No website snapshot yet"}</span>
        </div>
        <button type="button" onClick={() => sync().catch(() => undefined)} disabled={!canManage || !clientId}>Sync website data</button>
      </section>

      <section className="performance-status-grid">
        <article className="panel performance-status-card">
          <span>Cloudflare Web Analytics</span>
          <strong>{data?.client.website_url ? "Domain matched automatically" : "Add a live website URL"}</strong>
          <small>Visits and page views are pulled from the Cloudflare account for this client’s saved domain.</small>
        </article>
        <ConnectionStatus label="Search Console" source={searchSource} empty="Add the API site property, then sync to confirm access." />
        <article className="panel performance-status-card">
          <span>Weekly routine</span>
          <strong>Website sync plus automation check-in</strong>
          <small>Website data is saved automatically each Monday. You only enter automation outcomes that are not connected yet.</small>
        </article>
      </section>

      <section className="split-panels performance-split">
        <form className="panel form-panel" onSubmit={saveConnections}>
          <h2>Website tracking</h2>
          <div className="tracking-provision"><strong>Cloudflare is the website source</strong><span>Website visits and page views are matched automatically from the client’s saved live website domain.</span></div>
          <label>
            Ownership
            <select value={ownershipModel} onChange={(event) => setOwnershipModel(event.target.value as TrackingOwnership)}>
              <option value="builtlocally_managed">BuiltLocally managed</option>
              <option value="client_owned">Client owned</option>
            </select>
          </label>
          <label>Search Console site property<input value={searchConsolePropertyUrl} onChange={(event) => setSearchConsolePropertyUrl(event.target.value)} placeholder="sc-domain:example.com" /></label>
          <p className="form-hint">Cloudflare tracking needs no client-side setup for websites in the Builtlocally Cloudflare account. Search Console is optional for Google search reporting.</p>
          <button type="submit" disabled={!canManage || savingConnection}>{savingConnection ? "Saving" : "Save tracking"}</button>
        </form>

        <section className="panel performance-snapshot">
          <div className="panel-header"><h2>Latest website snapshot</h2></div>
          {!latest ? <EmptyInline text="Connect tracking, then use Sync website data. The first automatic weekly sync runs after that." /> : null}
          {latest ? (
            <>
              <p className="snapshot-period">{latest.period_start} to {latest.period_end}</p>
              <div className="snapshot-metrics">
                <SnapshotMetric label="Sessions" value={latest.sessions} />
                <SnapshotMetric label="Active users" value={latest.active_users} />
                <SnapshotMetric label="Page views" value={latest.page_views} />
                <SnapshotMetric label="Key events" value={latest.key_events} />
                <SnapshotMetric label="Google clicks" value={latest.search_clicks} />
                <SnapshotMetric label="Search impressions" value={latest.search_impressions} />
              </div>
              {latest.search_ctr != null || latest.search_average_position != null ? <p className="snapshot-note">Search CTR: {percentage(latest.search_ctr)}. Average position: {formatDecimal(latest.search_average_position)}.</p> : null}
            </>
          ) : null}
        </section>
      </section>

      <section className="split-panels performance-split">
        <form className="panel form-panel automation-form" onSubmit={saveAutomation}>
          <h2>Weekly automation results</h2>
          <label>Automation name<input value={automationName} onChange={(event) => setAutomationName(event.target.value)} /></label>
          <label>
            Automation type
            <select value={automationType} onChange={(event) => setAutomationType(event.target.value)}>
              <option value="booking">Booking</option>
              <option value="form_enquiry">Form enquiry</option>
              <option value="missed_call">Missed call</option>
              <option value="review_request">Review request</option>
              <option value="crm_pipeline">CRM pipeline</option>
              <option value="other">Other</option>
            </select>
          </label>
          <div className="date-inputs">
            <label>Period start<input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></label>
            <label>Period end<input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></label>
          </div>
          <div className="automation-count-grid">
            <label>Enquiries<input type="number" min="0" value={enquiriesCount} onChange={(event) => setEnquiriesCount(event.target.value)} /></label>
            <label>Bookings<input type="number" min="0" value={bookingsCount} onChange={(event) => setBookingsCount(event.target.value)} /></label>
            <label>Calls<input type="number" min="0" value={callsCount} onChange={(event) => setCallsCount(event.target.value)} /></label>
            <label>Messages<input type="number" min="0" value={messagesCount} onChange={(event) => setMessagesCount(event.target.value)} /></label>
            <label>Completed actions<input type="number" min="0" value={actionsCompleted} onChange={(event) => setActionsCompleted(event.target.value)} /></label>
            <label>Errors<input type="number" min="0" value={errorCount} onChange={(event) => setErrorCount(event.target.value)} /></label>
          </div>
          <label>Notes<textarea value={automationNotes} onChange={(event) => setAutomationNotes(event.target.value)} placeholder="What happened, what was confirmed, and anything to investigate next week." /></label>
          <button type="submit" disabled={!canManage || savingAutomation}>{savingAutomation ? "Saving" : "Save automation results"}</button>
        </form>

        <section className="panel automation-history">
          <div className="panel-header"><h2>Recent automation evidence</h2></div>
          {data?.automationSnapshots.length === 0 ? <EmptyInline text="No automation results recorded yet." /> : null}
          {data?.automationSnapshots.slice(0, 8).map((snapshot: AutomationMetricSnapshot) => (
            <article className="automation-history-row" key={snapshot.id}>
              <div>
                <strong>{snapshot.automation_name}</strong>
                <span>{snapshot.period_start} to {snapshot.period_end}</span>
              </div>
              <span>{snapshot.bookings_count} bookings</span>
              <span>{snapshot.enquiries_count} enquiries</span>
              <span>{snapshot.actions_completed} actions</span>
            </article>
          ))}
        </section>
      </section>
    </div>
  );
}

function ConnectionStatus({ label, source, empty }: { label: string; source?: ClientPerformanceData["sources"][number]; empty: string }) {
  return (
    <article className="panel performance-status-card">
      <span>{label}</span>
      <strong>{source ? labelText(source.status) : "Not connected"}</strong>
      <small>{source?.last_error || source?.external_id || empty}</small>
    </article>
  );
}

function SnapshotMetric({ label, value }: { label: string; value?: number | null }) {
  return <div><span>{label}</span><strong>{Number(value || 0).toLocaleString()}</strong></div>;
}

export function ProofPage({
  clients,
  permissions,
  onNotice
}: {
  clients: ClientRecord[];
  permissions: Permissions | null;
  onNotice: (notice: string) => void;
}) {
  const [clientId, setClientId] = useState(clients.find((client) => proofReady(client))?.id || clients[0]?.id || "");
  const [proofPackets, setProofPackets] = useState<ProofPacket[]>([]);
  const [recommendations, setRecommendations] = useState<ProofRecommendation[]>([]);
  const [openPacket, setOpenPacket] = useState<ProofPacketDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const canManage = permissions?.canChangeClientStage;
  const warmLeads = clients.filter((client) => client.stage === "warm_prospect");

  async function loadProof() {
    if (!canManage) return;
    setLoading(true);
    try {
      const allProof = await getProofPackets();
      setProofPackets(allProof.proofPackets);
      const leadId = warmLeads[0]?.id;
      if (leadId) {
        const leadProof = await getProofPackets({ leadClientId: leadId });
        setRecommendations(leadProof.recommendations || []);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProof().catch(() => onNotice("Could not load proof packets."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, clients.length]);

  async function generate() {
    if (!clientId || !canManage) return;
    const result = await generateProofPacket(clientId);
    setProofPackets((current) => [result.proofPacket, ...current.filter((packet) => packet.id !== result.proofPacket.id)]);
    onNotice("Proof packet queued for owner review.");
    await loadProof();
  }

  async function approve(packet: ProofPacket) {
    const result = await updateProofPacketStatus(packet.id, "approved");
    setProofPackets((current) => current.map((item) => (item.id === packet.id ? result.proofPacket : item)));
    onNotice("Proof packet approved.");
  }

  async function attach(recommendation: ProofRecommendation) {
    await attachProofRecommendation(recommendation.id);
    setRecommendations((current) => current.map((item) => (item.id === recommendation.id ? { ...item, status: "attached" } : item)));
    onNotice("Proof attached to warm lead.");
  }

  async function open(packet: ProofPacket) {
    const detail = await getProofPacketDetail(packet.id);
    setOpenPacket(detail);
  }

  async function markUsed(packet: ProofPacket) {
    const result = await updateProofPacketStatus(packet.id, "used_in_sales");
    setProofPackets((current) => current.map((item) => (item.id === packet.id ? result.proofPacket : item)));
    onNotice("Proof marked as used in sales.");
  }

  return (
    <div className="page-stack">
      <PageHeader title="Proof Engine" description="Turn delivered work into evidence-backed sales assets and attach the right proof to warm leads." />
      <section className="split-panels">
        <form className="panel form-panel" onSubmit={(event) => { event.preventDefault(); generate().catch((error) => onNotice(error instanceof Error ? error.message : "Could not generate proof.")); }}>
          <h2>Generate Proof</h2>
          <label>
            Delivered client
            <select value={clientId} onChange={(event) => setClientId(event.target.value)} disabled={!canManage}>
              {clients.filter(proofReady).map((client) => <option value={client.id} key={client.id}>{client.display_name}</option>)}
            </select>
          </label>
          <p className="form-hint">Only paid, subscribed, website-created, and active clients can produce reusable proof.</p>
          <button type="submit" disabled={!canManage || !clientId}>Generate proof</button>
        </form>
        <section className="panel report-list">
          <div className="panel-header">
            <h2>Warm Lead Matches</h2>
            <span>{recommendations.length} suggested</span>
          </div>
          {recommendations.length === 0 ? <EmptyInline text={loading ? "Loading proof matches." : "Generate proof from a delivered client to unlock lead matches."} /> : null}
          {recommendations.map((recommendation) => (
            <div className="report-row proof-match-row" key={recommendation.id}>
              <span>{recommendation.proof_client_name || recommendation.title}</span>
              <strong>{recommendation.reason}</strong>
              <button type="button" onClick={() => attach(recommendation).catch(() => onNotice("Could not attach proof."))} disabled={recommendation.status === "attached"}>
                {recommendation.status === "attached" ? "Attached" : "Attach"}
              </button>
            </div>
          ))}
        </section>
      </section>
      <section className="panel proof-packet-list">
        <div className="panel-header">
          <h2>Proof Packets</h2>
          <span>{proofPackets.length} packets</span>
        </div>
        {proofPackets.length === 0 ? <EmptyInline text={loading ? "Loading proof packets." : "No proof packets generated yet."} /> : null}
        {proofPackets.map((packet) => (
          <article className="proof-card" key={packet.id}>
            <div>
              <span className={`status-pill proof-${packet.status}`}>{label(packet.status)}</span>
              <h3>{packet.title}</h3>
              <p>{packet.sales_snippet || "No sales snippet yet."}</p>
              <small>{packet.quality_notes || "Needs owner review before use."}</small>
            </div>
            <div className="proof-actions">
              <button type="button" className="secondary-action" onClick={() => open(packet).catch(() => onNotice("Could not open proof packet."))}>Open</button>
              {packet.status !== "approved" ? <button type="button" onClick={() => approve(packet).catch(() => onNotice("Could not approve proof."))}>Approve</button> : null}
              {packet.status === "approved" ? <button type="button" className="secondary-action" onClick={() => markUsed(packet).catch(() => onNotice("Could not update proof status."))}>Used in sales</button> : null}
            </div>
          </article>
        ))}
      </section>
      {openPacket ? <ProofPacketViewer detail={openPacket} onClose={() => setOpenPacket(null)} onNotice={onNotice} /> : null}
    </div>
  );
}

function ProofPacketViewer({ detail, onClose, onNotice }: { detail: ProofPacketDetail; onClose: () => void; onNotice: (message: string) => void }) {
  async function copy(value: string) {
    await navigator.clipboard.writeText(value);
    onNotice("Copied to clipboard.");
  }

  return (
    <section className="panel proof-viewer">
      <div className="panel-header">
        <div>
          <h2>{detail.proofPacket.title}</h2>
          <p className="proof-viewer-intro">Review the evidence first, then copy the version that fits the conversation.</p>
        </div>
        <button type="button" className="secondary-action" onClick={onClose}>Close</button>
      </div>
      <div className="proof-viewer-body">
        <section className="proof-evidence-list">
          <h3>Evidence used</h3>
          {detail.evidence.length === 0 ? <EmptyInline text="This packet has no evidence items yet." /> : null}
          {detail.evidence.map((item) => (
            <article className="evidence-item" key={item.id}>
              <strong>{item.claim}</strong>
              <span>{item.source_label}{item.source_date ? ` - ${shortDate(item.source_date)}` : ""}</span>
              <em>{item.confidence}</em>
            </article>
          ))}
        </section>
        <section className="proof-asset-list">
          {detail.assets.map((asset) => (
            <article className="proof-asset" key={asset.id}>
              <div className="proof-asset-header">
                <div>
                  <span className="status-pill neutral">{label(asset.asset_type)}</span>
                  <h3>{asset.title}</h3>
                </div>
                <button type="button" className="secondary-action" onClick={() => copy(asset.content_md).catch(() => onNotice("Could not copy this asset."))}>Copy</button>
              </div>
              <pre>{asset.content_md}</pre>
            </article>
          ))}
        </section>
      </div>
    </section>
  );
}

export function PulsePage({
  clients,
  permissions,
  onNotice
}: {
  clients: ClientRecord[];
  permissions: Permissions | null;
  onNotice: (notice: string) => void;
}) {
  const [clientId, setClientId] = useState(clients.find((client) => client.stage === "active")?.id || clients[0]?.id || "");
  const [pulsePackets, setPulsePackets] = useState<PulsePacket[]>([]);
  const [loading, setLoading] = useState(false);
  const canManage = permissions?.canChangeClientStage;

  async function loadPulse() {
    if (!canManage) return;
    setLoading(true);
    try {
      const data = await getPulsePackets();
      setPulsePackets(data.pulsePackets);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPulse().catch(() => onNotice("Could not load Pulse packets."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, clients.length]);

  async function generate() {
    if (!clientId || !canManage) return;
    const result = await generatePulsePacket(clientId);
    setPulsePackets((current) => [result.pulsePacket, ...current.filter((packet) => packet.id !== result.pulsePacket.id)]);
    onNotice("Pulse packet queued for owner review.");
  }

  async function approve(packet: PulsePacket) {
    const result = await updatePulsePacketStatus(packet.id, "approved");
    setPulsePackets((current) => current.map((item) => (item.id === packet.id ? result.pulsePacket : item)));
    onNotice("Pulse packet approved.");
  }

  return (
    <div className="page-stack">
      <PageHeader title="Pulse" description="Draft plain-English client updates and referral asks from CRM stats, tracking notes, and automation reports." />
      <section className="split-panels">
        <form className="panel form-panel" onSubmit={(event) => { event.preventDefault(); generate().catch((error) => onNotice(error instanceof Error ? error.message : "Could not generate Pulse.")); }}>
          <h2>Generate Pulse</h2>
          <label>
            Client
            <select value={clientId} onChange={(event) => setClientId(event.target.value)} disabled={!canManage}>
              {clients.map((client) => <option value={client.id} key={client.id}>{client.display_name}</option>)}
            </select>
          </label>
          <p className="form-hint">Pulse drafts are review-only. Nothing is sent automatically.</p>
          <button type="submit" disabled={!canManage || !clientId}>Generate Pulse</button>
        </form>
        <section className="panel">
          <h2>Quality Rules</h2>
          <div className="quality-list">
            <span>Plain English only</span>
            <span>No fake numbers</span>
            <span>Referral ask included</span>
            <span>Owner approval before send</span>
          </div>
        </section>
      </section>
      <section className="panel proof-packet-list">
        <div className="panel-header">
          <h2>Pulse Packets</h2>
          <span>{pulsePackets.length} drafts</span>
        </div>
        {pulsePackets.length === 0 ? <EmptyInline text={loading ? "Loading Pulse packets." : "No Pulse packets generated yet."} /> : null}
        {pulsePackets.map((packet) => (
          <article className="pulse-card" key={packet.id}>
            <div className="pulse-card-main">
              <span className={`status-pill pulse-${packet.status}`}>{label(packet.status)}</span>
              <h3>{packet.title}</h3>
              <p>{packet.plain_english_summary}</p>
              <details>
                <summary>Preview email</summary>
                <strong>{packet.email_subject}</strong>
                <pre>{packet.email_body}</pre>
              </details>
              <small>{packet.quality_notes || "Needs owner review before sending."}</small>
            </div>
            {packet.status !== "approved" ? <button type="button" onClick={() => approve(packet).catch(() => onNotice("Could not approve Pulse."))}>Approve</button> : null}
          </article>
        ))}
      </section>
    </div>
  );
}

export function SettingsPage({ onNotice, permissions }: { onNotice: (notice: string) => void; permissions: Permissions | null }) {
  const [sources, setSources] = useState<CallerSheetSource[]>([]);
  const [config, setConfig] = useState<GoogleSheetsConfig | null>(null);
  const [name, setName] = useState("");
  const [spreadsheetId, setSpreadsheetId] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [rangeName, setRangeName] = useState("A:Z");
  const owner = permissions?.canManageUsers;

  async function loadSettings() {
    if (!owner) return;
    setSources(await getCallerSheetSources());
    setConfig(await getGoogleSheetsConfig());
  }

  async function submitSheet(event: FormEvent) {
    event.preventDefault();
    if (!owner) return;
    const created = await registerCallerSheet({ name, spreadsheetId, sheetName, rangeName });
    onNotice(`Registered sheet ${created.id}.`);
    setName("");
    setSpreadsheetId("");
    setSheetName("");
    setSources(await getCallerSheetSources());
  }

  async function handleImport(kind: "master" | "b-online", file: File | null) {
    if (!file || !owner) return;
    const result = await importCsv(kind, file);
    onNotice(`Imported ${result.rowsImported ?? 0}; updated ${result.rowsUpdated ?? 0}; seen ${result.rowsSeen ?? 0}.`);
  }

  async function runSync() {
    if (!owner) return;
    const result = await syncGoogleSheets();
    onNotice(`Sync complete: ${result.results.length} source(s) checked.`);
  }

  async function runTelegram() {
    if (!owner) return;
    await processTelegramOutbox();
    onNotice("Telegram outbox checked.");
  }

  return (
    <div className="page-stack">
      <PageHeader title="Settings" description="Owner-only imports, sync setup, notification processing, and deployment checks." action={<button className="primary-action" onClick={loadSettings} disabled={!owner}>Load settings</button>} />
      {!owner ? <section className="panel settings-list"><p>User accounts cannot access import, sync, or secret setup controls.</p></section> : null}
      {owner ? (
        <>
          <section className="sheet-setup">
            <article className={config?.configured ? "setup-card ready" : "setup-card"}>
              <Icon name={config?.configured ? "sync" : "alert"} />
              <div>
                <strong>{config?.configured ? "Google sync credentials are ready" : "Google sync credentials not loaded"}</strong>
                <span>{config?.configured ? `Share sheets with ${config.serviceAccountEmail}.` : "Load settings to check Google service account configuration."}</span>
              </div>
            </article>
            <article className="setup-card ready">
              <Icon name="bell" />
              <div>
                <strong>Outbox processors</strong>
                <span>Telegram and report processors run from existing owner-only API routes.</span>
              </div>
            </article>
          </section>
          <section className="split-panels">
            <form className="panel form-panel" onSubmit={submitSheet}>
              <h2>Add Google Sheet Source</h2>
              <label>Source name<input value={name} onChange={(event) => setName(event.target.value)} /></label>
              <label>Google Sheet link or ID<input value={spreadsheetId} onChange={(event) => setSpreadsheetId(event.target.value)} /></label>
              <label>Sheet tab name<input value={sheetName} onChange={(event) => setSheetName(event.target.value)} /></label>
              <label>Range<input value={rangeName} onChange={(event) => setRangeName(event.target.value)} /></label>
              <button type="submit">Register source</button>
            </form>
            <section className="panel source-list">
              <div className="panel-header">
                <h2>Operations</h2>
                <button onClick={runTelegram}>Process Telegram</button>
              </div>
              <div className="settings-list">
                <label className="file-button">Import master CSV<input type="file" accept=".csv,text/csv" onChange={(event) => handleImport("master", event.currentTarget.files?.[0] ?? null)} /></label>
                <label className="file-button">Import B Online CSV<input type="file" accept=".csv,text/csv" onChange={(event) => handleImport("b-online", event.currentTarget.files?.[0] ?? null)} /></label>
                <button className="secondary-action" onClick={runSync}>Sync Google Sheets</button>
              </div>
              {sources.map((source) => (
                <article className="source-item" key={source.id}>
                  <Icon name="sheet" />
                  <div>
                    <strong>{source.name}</strong>
                    <span>{source.sheet_name || "First tab"} - {source.range_name}</span>
                  </div>
                  <em>{source.last_synced_at ? new Date(source.last_synced_at).toLocaleString() : "Not synced"}</em>
                </article>
              ))}
            </section>
          </section>
        </>
      ) : null}
    </div>
  );
}

function EntityTable({
  type,
  rows,
  selectedId,
  onSelect,
  columns
}: {
  type: WarmEntityType;
  rows: WarmEntity[];
  selectedId?: string;
  onSelect: (entity: WarmEntity) => void;
  columns: string[];
}) {
  return (
    <section className="panel table-panel entity-table-panel">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{type === "client" ? "Client" : type === "partner" ? "Partner" : "Node"}</th>
              {columns.map((column) => <th key={column}>{column}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td className="empty-table" colSpan={columns.length + 1}>No records match this view.</td></tr> : null}
            {rows.map((row) => (
              <tr className={row.id === selectedId ? "selected" : ""} key={row.id} onClick={() => onSelect(row)}>
                <td data-label={type === "client" ? "Client" : type === "partner" ? "Partner" : "Node"}>
                  <strong>{entityName(row)}</strong>
                  <small>{entitySubtitle(row)}</small>
                </td>
                {entityCells(type, row).map((cell, index) => <td data-label={columns[index]} key={`${row.id}-${index}`}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ClientCreateForm({
  clients,
  partners,
  nodes,
  permissions,
  onCreated
}: {
  clients: ClientRecord[];
  partners: PartnerRecord[];
  nodes: NodeRecord[];
  permissions: Permissions | null;
  onCreated: (client: ClientRecord) => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [businessCategory, setBusinessCategory] = useState("");
  const [cityArea, setCityArea] = useState("");
  const [sourceKind, setSourceKind] = useState("manual");
  const [referrerId, setReferrerId] = useState("");
  const [justification, setJustification] = useState("");
  const [saving, setSaving] = useState(false);
  const owner = permissions?.canChangeClientStage;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!displayName || (!phoneNumber && !email) || (!owner && !justification)) return;
    const referrer = sourceKind === "partner_referral"
      ? partners.find((item) => item.id === referrerId)
      : sourceKind === "node_referral"
        ? nodes.find((item) => item.id === referrerId)
        : sourceKind === "client_referral"
          ? clients.find((item) => item.id === referrerId)
          : undefined;
    if (["partner_referral", "node_referral", "client_referral"].includes(sourceKind) && !referrer) return;
    setSaving(true);
    try {
      const client = await createClient({
        displayName,
        contactName,
        phoneNumber,
        email,
        websiteUrl,
        businessCategory,
        cityArea,
        sourceKind,
        referrerName: referrer ? entityName(referrer) : "",
        referrerEntityType: referrer ? (sourceKind.replace("_referral", "") as "client" | "partner" | "node") : "",
        referrerEntityId: referrer?.id || "",
        candidateJustification: justification,
        stage: owner ? "warm_prospect" : undefined
      });
      onCreated(client);
      setDisplayName("");
      setContactName("");
      setPhoneNumber("");
      setEmail("");
      setWebsiteUrl("");
      setBusinessCategory("");
      setCityArea("");
      setSourceKind("manual");
      setReferrerId("");
      setJustification("");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="panel inline-create" onSubmit={submit}>
      <h2>Add warm lead</h2>
      <label>Business name<input required value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
      <label>Contact name<input value={contactName} onChange={(event) => setContactName(event.target.value)} /></label>
      <label>Phone<input value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} /></label>
      <label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
      <label>Website<input type="url" value={websiteUrl} onChange={(event) => setWebsiteUrl(event.target.value)} placeholder="https://" /></label>
      <label>Business type / niche<input value={businessCategory} onChange={(event) => setBusinessCategory(event.target.value)} /></label>
      <label>Town or area<input value={cityArea} onChange={(event) => setCityArea(event.target.value)} /></label>
      <label>
        Lead source
        <select value={sourceKind} onChange={(event) => { setSourceKind(event.target.value); setReferrerId(""); }}>
          <option value="manual">Direct research or inbound</option>
          <option value="partner_referral">Partner referral</option>
          <option value="node_referral">Node referral</option>
          <option value="client_referral">Client referral</option>
        </select>
      </label>
      {sourceKind !== "manual" ? (
        <label>
          Referrer
          <select required value={referrerId} onChange={(event) => setReferrerId(event.target.value)}>
            <option value="">Select a referrer</option>
            {(sourceKind === "partner_referral" ? partners : sourceKind === "node_referral" ? nodes : clients).map((item) => <option key={item.id} value={item.id}>{entityName(item)}</option>)}
          </select>
        </label>
      ) : null}
      <label>Research / context<input required={!owner} value={justification} onChange={(event) => setJustification(event.target.value)} placeholder="Why is this a good lead and what should the first message mention?" /></label>
      <button type="submit" disabled={saving}>{saving ? "Saving" : "Add warm lead"}</button>
    </form>
  );
}

function RelationshipCreateForm({ type, onCreated }: { type: "partner" | "node"; onCreated: (entity: PartnerRecord | NodeRecord) => void }) {
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [websiteOrSocial, setWebsiteOrSocial] = useState("");
  const [cityArea, setCityArea] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [relationshipType, setRelationshipType] = useState(type === "partner" ? "business" : "connector");
  const [justification, setJustification] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!name || !justification) return;
    setSaving(true);
    try {
      const entity = type === "partner"
        ? await createPartner({
            name,
            primaryContactName: contactName,
            phoneNumber,
            email,
            websiteUrl: websiteOrSocial,
            cityArea,
            partnerType: relationshipType,
            candidateJustification: justification
          })
        : await createNode({
            name,
            organizationName,
            roleTitle,
            phoneNumber,
            email,
            socialUrl: websiteOrSocial,
            cityArea,
            nodeType: relationshipType,
            candidateJustification: justification
          });
      onCreated(entity);
      setName("");
      setContactName("");
      setPhoneNumber("");
      setEmail("");
      setWebsiteOrSocial("");
      setCityArea("");
      setOrganizationName("");
      setRoleTitle("");
      setRelationshipType(type === "partner" ? "business" : "connector");
      setJustification("");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="panel inline-create" onSubmit={submit}>
      <h2>Add {type === "partner" ? "partner" : "node"}</h2>
      <label>Name<input required value={name} onChange={(event) => setName(event.target.value)} /></label>
      {type === "partner" ? <label>Primary contact<input value={contactName} onChange={(event) => setContactName(event.target.value)} /></label> : null}
      {type === "node" ? <label>Organisation / community<input value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} /></label> : null}
      {type === "node" ? <label>Role or title<input value={roleTitle} onChange={(event) => setRoleTitle(event.target.value)} /></label> : null}
      <label>Phone<input value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} /></label>
      <label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
      <label>{type === "partner" ? "Website" : "Website or social link"}<input type="url" value={websiteOrSocial} onChange={(event) => setWebsiteOrSocial(event.target.value)} placeholder="https://" /></label>
      <label>Town or area<input value={cityArea} onChange={(event) => setCityArea(event.target.value)} /></label>
      <label>
        {type === "partner" ? "Partner type" : "Node type"}
        <select value={relationshipType} onChange={(event) => setRelationshipType(event.target.value)}>
          {type === "partner" ? <>
            <option value="business">Business</option><option value="agency">Agency</option><option value="community_org">Community organisation</option><option value="supplier">Supplier</option><option value="creator">Creator</option><option value="professional_service">Professional service</option><option value="other">Other</option>
          </> : <>
            <option value="connector">Connector</option><option value="community_leader">Community leader</option><option value="church_leader">Church leader</option><option value="event_organizer">Event organiser</option><option value="business_owner">Business owner</option><option value="gatekeeper">Gatekeeper</option><option value="other">Other</option>
          </>}
        </select>
      </label>
      <label>Why they matter / useful context<input required value={justification} onChange={(event) => setJustification(event.target.value)} /></label>
      <button type="submit" disabled={saving}>{saving ? "Saving" : `Add ${type}`}</button>
    </form>
  );
}

function StageBoard({ clients, onSelect }: { clients: ClientRecord[]; onSelect: (client: ClientRecord) => void }) {
  const stages = ["warm_prospect", "discovery_booked", "proposal_sent", "paid", "subscribed", "website_created", "active"] as const;
  return (
    <div className="stage-board">
      {stages.map((stage) => {
        const stageClients = clients.filter((client) => client.stage === stage);
        return (
          <div className="stage-column" key={stage}>
            <header>
              <strong>{label(stage)}</strong>
              <span>{stageClients.length}</span>
            </header>
            {stageClients.map((client) => (
              <button className="stage-card" key={client.id} onClick={() => onSelect(client)}>
                <strong>{client.display_name}</strong>
                <span>{client.next_follow_up_at ? `Next ${shortDate(client.next_follow_up_at)}` : client.source_kind || "manual"}</span>
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function EventList({ events, compact = false }: { events: CrmEvent[]; compact?: boolean }) {
  return (
    <div className={compact ? "event-list compact" : "event-list"}>
      {events.length === 0 ? <EmptyInline text="No events are visible yet." /> : null}
      {events.map((event) => (
        <article className="event-row" key={event.id}>
          <Icon name={event.event_type.includes("stage") ? "sync" : "clock"} />
          <div>
            <strong>{event.title}</strong>
            <span>{event.actor_label || event.source || "System"} - {shortDate(event.occurred_at)}</span>
          </div>
          {!compact ? <em>{event.entity_type}</em> : null}
        </article>
      ))}
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="kpi">
      <div>
        <span>{label}</span>
        <strong>{value.toLocaleString()}</strong>
        <small>{hint}</small>
      </div>
    </div>
  );
}

function EmptyInline({ text }: { text: string }) {
  return <p className="empty-inline">{text}</p>;
}

function filterRows<T extends WarmEntity>(rows: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => [entityName(row), entitySubtitle(row), row.city_area, row.email, row.phone_number].some((value) => String(value || "").toLowerCase().includes(q)));
}

export function entityName(entity: WarmEntity): string {
  if ("display_name" in entity) return entity.display_name;
  return entity.name;
}

function entitySubtitle(entity: WarmEntity): string {
  if ("display_name" in entity) return entity.business_category || entity.city_area || entity.email || "Client";
  if ("node_type" in entity) {
    const node = entity as NodeRecord;
    return node.organization_name || node.city_area || "Node";
  }
  const partner = entity as PartnerRecord;
  return partner.partner_type || partner.city_area || "Partner";
}

function entityCells(type: WarmEntityType, row: WarmEntity): React.ReactNode[] {
  if (type === "client") {
    const client = row as ClientRecord;
    return [
      <span className={`status-pill stage-${client.stage}`} key="stage">{label(client.stage)}</span>,
      label(client.service_type),
      client.contact_name || client.email || client.phone_number || "No contact",
      shortDate(client.next_follow_up_at),
      label(client.source_kind || "manual")
    ];
  }
  if (type === "partner") {
    const partner = row as PartnerRecord;
    return [
      <span className={`status-pill relationship-${partner.relationship_stage}`} key="stage">{label(partner.relationship_stage)}</span>,
      label(partner.partner_type || "business"),
      partner.primary_contact_name || partner.email || partner.phone_number || "No contact",
      shortDate(partner.next_follow_up_at),
      partner.referral_fit_notes || partner.candidate_justification || "Review fit"
    ];
  }
  const node = row as NodeRecord;
  return [
    <span className={`status-pill relationship-${node.relationship_stage}`} key="stage">{label(node.relationship_stage)}</span>,
    label(node.node_type || "connector"),
    node.role_title || node.email || node.phone_number || "No contact",
    shortDate(node.next_follow_up_at),
    node.community_context || node.referral_fit_notes || "Review context"
  ];
}

function label(value?: string | null) {
  return String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(" ") || "None";
}

function proofReady(client: ClientRecord) {
  return ["paid", "subscribed", "website_created", "active"].includes(client.stage);
}

const FOLLOW_UP_CLOSED_STAGES = new Set(["churned", "lost"]);

function splitFollowUps(clients: ClientRecord[]): { newLeads: ClientRecord[]; dueLeads: ClientRecord[] } {
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  const newLeads: ClientRecord[] = [];
  const dueLeads: ClientRecord[] = [];

  for (const client of clients) {
    if (FOLLOW_UP_CLOSED_STAGES.has(client.stage)) continue;

    // New = a warm prospect nobody has contacted yet.
    if (client.stage === "warm_prospect" && !client.last_contacted_at) {
      newLeads.push(client);
      continue;
    }

    // Due = a follow-up date that has arrived or passed.
    if (client.next_follow_up_at) {
      const due = new Date(client.next_follow_up_at);
      if (!Number.isNaN(due.getTime()) && due.getTime() <= endOfToday.getTime()) {
        dueLeads.push(client);
      }
    }
  }

  const byDueDate = (a: ClientRecord, b: ClientRecord) =>
    new Date(a.next_follow_up_at || 0).getTime() - new Date(b.next_follow_up_at || 0).getTime();
  dueLeads.sort(byDueDate);

  return { newLeads, dueLeads };
}

function shortDate(value?: string | null) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}

function weekInput(offsetDays: number) {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

function labelText(value?: string | null) {
  return String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(" ") || "Not connected";
}

function percentage(value?: number | null) {
  return value == null ? "Not available" : `${(value * 100).toFixed(1)}%`;
}

function formatDecimal(value?: number | null) {
  return value == null ? "Not available" : value.toFixed(1);
}
